import { observer } from 'mobx-react-lite';
import React, { useContext } from 'react';
import { DialogContext } from '../../../../store/context/DialogContext';
import ButtonGroupNew from '../BusinessLiteForms/components/ButtonGroupNew';
import BusinessV2FieldGrid from './BusinessV2FieldGrid';
import { collectVisibleFieldErrors, getFieldErrorValue, getNextStepV2, getVisibleWizardFieldOrder, V2_TYPE } from './helperV2';

const MoneyServicesBusiness = ({
    asLabel,
    hideButtons,
    data,
    handleUpdate,
    hasError,
    setHasError,
    loading,
    cyclosFields,
    errors,
    setErrors,
    iPlusFields,
    wizard,
    applicationForm,
    isForEDD,
    handleChange,
    handlePrev,
    setData,
    editMode,
    formType,
    jsonConfig,
    pageNum = 1,
}) => {
    const { showError } = useContext(DialogContext);
    const applicationType = Number(applicationForm?.type);
    const wizardContent = jsonConfig?.wizardContent?.[wizard] || {};
    const visibleFieldOrder = (() => {
        if (applicationType === V2_TYPE.PLUS) return [];
        return getVisibleWizardFieldOrder({
            fields: iPlusFields,
            wizard,
            data,
            type: applicationType,
        });
    })();
    const sectionBreaks = Array.isArray(wizardContent?.sectionBreaks) ? wizardContent.sectionBreaks : [];
    const firstSectionBreak = sectionBreaks.find((sectionBreak) => (
        typeof sectionBreak?.beforeField === 'string'
        && visibleFieldOrder.includes(sectionBreak.beforeField)
    )) || null;
    const sectionBreakIndex = firstSectionBreak?.beforeField
        ? visibleFieldOrder.indexOf(firstSectionBreak.beforeField)
        : -1;
    const introFieldOrder = sectionBreakIndex === -1
        ? visibleFieldOrder
        : visibleFieldOrder.slice(0, sectionBreakIndex);
    const sectionBreakFieldOrder = sectionBreakIndex === -1
        ? []
        : visibleFieldOrder.slice(sectionBreakIndex);

    if (applicationType === V2_TYPE.PLUS) {
        return null;
    }

    const checkNext = () => {
        const { fieldErrors } = collectVisibleFieldErrors({
            data,
            step: { wizard },
            iPlusFields,
            errors,
            type: applicationType,
        });
        const mergedErrors = {
            ...(errors || {}),
            ...fieldErrors,
        };
        setErrors(mergedErrors);

        for (const fieldPath of visibleFieldOrder) {
            const fieldError = getFieldErrorValue(mergedErrors, fieldPath);
            if (fieldError) {
                setHasError(true);
                if (fieldError) {
                    showError(fieldError);
                    return false;
                }
            }
        }

        setErrors({});
        return true;
    };

    const handleSubmit = () => {
        const isFormFieldValid = checkNext();
        if (isFormFieldValid) {
            setHasError(false);
            return handleUpdate({ nextStep: getNextStepV2(wizard, applicationType, data, jsonConfig) });

        }
    };

    return (
        <div className="business-v2-step-shell">
                <div className='business-v2-step-section'>
                    <h2 className='business-v2-step-title'>Money Services Business</h2>
                    {wizardContent?.intro?.title && (
                        <h3 className='business-v2-step-title'>
                            {wizardContent.intro.title}
                        </h3>
                    )}
                    {(wizardContent?.intro?.paragraphs || []).map((paragraph, index) => (
                        <p key={`msb-intro-${index}`} className='business-v2-step-copy'>
                            {paragraph}
                        </p>
                    ))}

                    {!!introFieldOrder.length && (
                        <BusinessV2FieldGrid
                            fieldOrder={introFieldOrder}
                            fieldMap={cyclosFields}
                            data={data}
                            asLabel={asLabel}
                            handleChange={handleChange}
                            hasError={hasError}
                            onFieldError={(field, errorMsg) => setErrors((prevErrors) => ({ ...(prevErrors || {}), [field]: errorMsg }))}
                            dataTestId='business-v2-money-services-business-fields-primary'
                        />
                    )}

                    {!!sectionBreakFieldOrder.length && (
                        <>
                            {firstSectionBreak?.title && (
                                <h3 className='business-v2-step-title'>
                                    {firstSectionBreak.title}
                                </h3>
                            )}
                            {(firstSectionBreak?.paragraphs || []).map((paragraph, index) => (
                                <p key={`msb-break-${index}`} className='business-v2-step-copy'>
                                    {paragraph}
                                </p>
                            ))}

                            <BusinessV2FieldGrid
                                fieldOrder={sectionBreakFieldOrder}
                                fieldMap={cyclosFields}
                                data={data}
                                asLabel={asLabel}
                                handleChange={handleChange}
                                hasError={hasError}
                                onFieldError={(field, errorMsg) => setErrors((prevErrors) => ({ ...(prevErrors || {}), [field]: errorMsg }))}
                                dataTestId='business-v2-money-services-business-fields-secondary'
                            />
                        </>
                    )}
                </div>

                <div className='business-v2-step-actions'>
                    <ButtonGroupNew
                        onNextClick={handleSubmit}
                        onBackClick={() => { handlePrev() }}
                        step={pageNum}
                        showPageNum={false}
                        pageNum={pageNum}
                        hideSubmitButton={hideButtons}
                        asLabel={false}
                        loading={loading}
                    />
                </div>
        </div>
    );
};

export default observer(MoneyServicesBusiness);
